import React, { useMemo, useState } from 'react';
import {
  Download,
  ExternalLink,
  FileText,
  Loader2,
  Upload,
} from 'lucide-react';

import AddStaffDocumentModal from './AddStaffDocumentModal';
import { staffApi } from '../../services/staffApi';
import { getStaffApiErrorMessage } from '../../utils/staffErrors';

const documentTypeLabel = (value) =>
  String(value || 'Document')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const StaffDocumentsTab = ({
  formData,
  schoolId,
  staffId,
  loadStaff,
  showToast,
}) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);

  const documents = useMemo(
    () => (Array.isArray(formData?.documents) ? formData.documents : []),
    [formData?.documents],
  );

  const handleUploadDocument = async (uploadData) => {
    if (uploading) return;

    try {
      setUploading(true);

      await staffApi.uploadStaffDocument(
        schoolId,
        staffId,
        uploadData,
      );

      await loadStaff();
      setShowUploadModal(false);
      showToast('Document uploaded successfully.', 'success');
    } catch (error) {
      console.error('Failed to upload document', error);
      showToast(
        getStaffApiErrorMessage(error, 'Unable to upload the staff document.'),
        'error',
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-gray-100 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Documents
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              {documents.length} document{documents.length === 1 ? '' : 's'} available
            </p>
          </div>

          <button
            type="button"
            onClick={() => setShowUploadModal(true)}
            disabled={uploading}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Upload Document
          </button>
        </div>

        {documents.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
            <div className="rounded-full bg-blue-50 p-4 text-blue-600">
              <FileText className="h-8 w-8" />
            </div>
            <h3 className="mt-4 text-base font-semibold text-gray-900">
              No documents uploaded
            </h3>
            <p className="mt-2 max-w-md text-sm leading-6 text-gray-500">
              Upload appointment letters, qualification certificates, identity proof, or other staff documents.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 xl:grid-cols-3">
            {documents.map((document) => (
              <article
                key={document.id}
                className="rounded-2xl border border-gray-200 p-5 transition hover:border-blue-200 hover:shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <div className="rounded-xl bg-blue-50 p-3 text-blue-600">
                    <FileText className="h-5 w-5" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-sm font-semibold text-gray-900">
                      {document.fileName || documentTypeLabel(document.documentType)}
                    </h3>
                    <p className="mt-1 text-xs text-gray-500">
                      {documentTypeLabel(document.documentType)}
                    </p>
                  </div>

                  <span
                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                      document.verificationStatus === 'APPROVED'
                        ? 'bg-green-100 text-green-700'
                        : document.verificationStatus === 'REJECTED'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {document.verificationStatus || 'PENDING'}
                  </span>
                </div>

                <dl className="mt-5 space-y-2 text-xs">
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-500">Document number</dt>
                    <dd className="text-right font-medium text-gray-800">
                      {document.documentNumber || '—'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-500">Issued date</dt>
                    <dd className="text-right font-medium text-gray-800">
                      {formatDate(document.issuedDate)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-500">Expiry date</dt>
                    <dd className="text-right font-medium text-gray-800">
                      {formatDate(document.expiryDate)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-500">Uploaded</dt>
                    <dd className="text-right font-medium text-gray-800">
                      {formatDate(document.createdAt)}
                    </dd>
                  </div>
                </dl>

                <div className="mt-5 flex gap-2 border-t border-gray-100 pt-4">
                  {document.fileUrl ? (
                    <>
                      <a
                        href={document.fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-blue-200 px-3 py-2 text-xs font-semibold text-blue-600 hover:bg-blue-50"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        View
                      </a>
                      <a
                        href={document.fileUrl}
                        download={document.fileName || true}
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                      >
                        <Download className="h-3.5 w-3.5" />
                        Download
                      </a>
                    </>
                  ) : (
                    <p className="w-full rounded-lg bg-amber-50 px-3 py-2 text-center text-xs text-amber-700">
                      File URL is unavailable.
                    </p>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <AddStaffDocumentModal
        isOpen={showUploadModal}
        onClose={() => {
          if (!uploading) setShowUploadModal(false);
        }}
        onSubmit={handleUploadDocument}
        loading={uploading}
      />
    </div>
  );
};

export default StaffDocumentsTab;
